import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
  BREAKPOINT_1,
  BREAKPOINT_2,
  BREAKPOINT_3,
  BREAKPOINT_4,
  DRAWER_SEARCH,
} from "@/const";
import { useUser } from "./context";

interface ContextValue {
  colsPerRow: number;
  condensedNavBar: boolean;
  openDrawer: string;
  setOpenDrawer: Dispatch<SetStateAction<string>>;
  drawerWidth: string;
  drawerMargin: string | undefined;
  showOverlay: boolean;
  transitionStyle: string;
  isFullscreen: boolean;
  setIsFullscreen: Dispatch<SetStateAction<boolean>>;
  setResubscribeBannerDismissed: Dispatch<SetStateAction<boolean>>;
  showResubscribeBanner: boolean;
}

type OptionalContextValue = ContextValue | undefined;

const ResponsivenessContext = createContext<OptionalContextValue>(undefined);
const ResponsivenessProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { detailed_products } = useUser();

  const [colsPerRow, setColsPerRow] = useState(3);
  const [drawerWidth, setDrawerWidth] = useState("480px");
  const [condensedNavBar, setCondensedNavBar] = useState(false);
  const [openDrawer, setOpenDrawer] = useState<string>("");
  const [hasDrawerMargin, setHasDrawerMargin] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [resubscribeBannerDismissed, setResubscribeBannerDismissed] =
    useState(false);
  const initialWidth = useRef<number | null>(null);

  const showResubscribeBanner = useMemo(() => {
    if (resubscribeBannerDismissed) {
      return false;
    }

    if (!detailed_products || detailed_products.length !== 1) {
      return false;
    }
    return detailed_products[0]?.old_platform === "Resubscribe";
  }, [detailed_products, resubscribeBannerDismissed]);

  useEffect(() => {
    if (initialWidth.current === null) {
      initialWidth.current = window.innerWidth;
    }
  }, []);

  // Search screen drawer should start open if the window is wide enough
  // Use initialWidth to avoid drawer opening/closing when window is resized
  useEffect(() => {
    if (pathname === "/search" && initialWidth.current) {
      setOpenDrawer(initialWidth.current < BREAKPOINT_2 ? "" : DRAWER_SEARCH);
    } else {
      setOpenDrawer("");
    }
  }, [pathname]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width >= BREAKPOINT_4) {
        setColsPerRow(openDrawer ? 4 : 6);
        setDrawerWidth("480px");
      } else if (width >= BREAKPOINT_3) {
        setColsPerRow(openDrawer ? 3 : 5);
        setDrawerWidth("430px");
      } else if (width >= BREAKPOINT_2) {
        setColsPerRow(openDrawer ? 2 : 4);
        setDrawerWidth("380px");
      } else if (width >= BREAKPOINT_1) {
        setColsPerRow(4);
        setDrawerWidth("380px");
      } else {
        setColsPerRow(3);
        setDrawerWidth("380px");
      }

      setCondensedNavBar(width < BREAKPOINT_2);
      setShowOverlay(width < BREAKPOINT_2);
      setHasDrawerMargin(() => {
        if (!openDrawer) {
          return false;
        }
        return window.innerWidth >= BREAKPOINT_2;
      });
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [openDrawer]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [setIsFullscreen]);

  return (
    <ResponsivenessContext.Provider
      value={{
        colsPerRow,
        condensedNavBar,
        openDrawer,
        setOpenDrawer,
        showOverlay,
        drawerWidth,
        drawerMargin: hasDrawerMargin ? drawerWidth : undefined,
        transitionStyle: "margin-right 0ms ease-in-out", // Eases margin changes smoothly, ties in with Drawer component transition
        isFullscreen,
        setIsFullscreen,
        setResubscribeBannerDismissed,
        showResubscribeBanner,
      }}
    >
      {children}
    </ResponsivenessContext.Provider>
  );
};
export { ResponsivenessContext, ResponsivenessProvider };
